import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import type { RootState } from "@/app/store/store";
import { STATUS } from "@/constants";
import { apiFetch } from "@/shared/api/apiFetch";
import { readApiErrorMessage } from "@/shared/api/parseApiErrorMessage";

/**
 * Indexing state of one PDF inside the domain's Bedrock Knowledge Base. `status` is Bedrock's own
 * vocabulary (INDEXED, PENDING, FAILED, IGNORED, ...) passed through untranslated, so a status
 * this build has never seen still reaches the user instead of being dropped.
 */
export type DomainDocumentEmbeddingFileStatus = {
  filename: string;
  status: string;
  status_reason: string | null;
  updated_at: string | null;
};

/**
 * Live embedding state for a domain, derived entirely from Bedrock — there is no tracking table
 * behind it, so this can never disagree with what the Knowledge Base actually holds. Every count
 * describes this domain alone, even when several domains share one Knowledge Base.
 */
export type DomainDocumentEmbeddingStatus = {
  domain_id: number;
  is_running: boolean;
  document_count: number;
  indexed_count: number;
  pending_count: number;
  failed_count: number;
  documents: DomainDocumentEmbeddingFileStatus[];
};

export type DomainDocumentEmbeddingsState = {
  statusByDomainId: Record<number, DomainDocumentEmbeddingStatus>;
  /** Load state of the status read, per domain, so one domain's failure is not another's. */
  requestStatusByDomainId: Record<number, "idle" | "loading" | "succeeded" | "failed">;
  /** Why the last status read failed, per domain. */
  loadErrorByDomainId: Record<number, string | null>;
  /** Why the last start request failed, per domain (already-running, missing config, ...). */
  startErrorByDomainId: Record<number, string | null>;
  startingDomainIds: number[];
};

export const domainDocumentEmbeddingsInitialState: DomainDocumentEmbeddingsState = {
  statusByDomainId: {},
  requestStatusByDomainId: {},
  loadErrorByDomainId: {},
  startErrorByDomainId: {},
  startingDomainIds: [],
};

function isEmbeddingFileStatus(value: unknown): value is DomainDocumentEmbeddingFileStatus {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  const row = value as Record<string, unknown>;
  return (
    typeof row.filename === "string" &&
    typeof row.status === "string" &&
    (row.status_reason === null || typeof row.status_reason === "string") &&
    (row.updated_at === null || typeof row.updated_at === "string")
  );
}

function parseEmbeddingStatus(data: unknown): DomainDocumentEmbeddingStatus | null {
  if (typeof data !== "object" || data === null) {
    return null;
  }
  const row = data as Record<string, unknown>;
  if (
    typeof row.domain_id !== "number" ||
    typeof row.is_running !== "boolean" ||
    typeof row.document_count !== "number" ||
    typeof row.indexed_count !== "number" ||
    typeof row.pending_count !== "number" ||
    typeof row.failed_count !== "number"
  ) {
    return null;
  }
  if (!Array.isArray(row.documents) || !row.documents.every(isEmbeddingFileStatus)) {
    return null;
  }
  return data as DomainDocumentEmbeddingStatus;
}

export const startDomainDocumentEmbedding = createAsyncThunk<
  number,
  number,
  { rejectValue: string }
>("domainDocumentEmbeddings/start", async (domainId, { rejectWithValue }) => {
  try {
    const res = await apiFetch(`/v1/domains/${domainId}/documents/embeddings`, {
      method: "POST",
    });
    if (!res.ok) {
      return rejectWithValue(await readApiErrorMessage(res));
    }
    return domainId;
  } catch (error) {
    const message = error instanceof Error ? error.message : "Request failed";
    return rejectWithValue(message);
  }
});

export const fetchDomainDocumentEmbeddingStatus = createAsyncThunk<
  { domainId: number; status: DomainDocumentEmbeddingStatus },
  number,
  { rejectValue: { domainId: number; message: string } }
>("domainDocumentEmbeddings/fetchStatus", async (domainId, { rejectWithValue }) => {
  try {
    const res = await apiFetch(`/v1/domains/${domainId}/documents/embeddings`, {
      method: "GET",
    });
    if (!res.ok) {
      return rejectWithValue({ domainId, message: await readApiErrorMessage(res) });
    }
    const data: unknown = await res.json();
    const status = parseEmbeddingStatus(data);
    if (!status) {
      return rejectWithValue({ domainId, message: "Unexpected response shape" });
    }
    return { domainId, status };
  } catch (error) {
    const message = error instanceof Error ? error.message : "Request failed";
    return rejectWithValue({ domainId, message });
  }
});

const domainDocumentEmbeddingsSlice = createSlice({
  name: "domainDocumentEmbeddings",
  initialState: domainDocumentEmbeddingsInitialState,
  reducers: {
    clearDomainDocumentEmbeddingStartError(state, action: { payload: number }) {
      state.startErrorByDomainId[action.payload] = null;
    },
  },
  extraReducers(builder) {
    builder
      .addCase(startDomainDocumentEmbedding.pending, (state, action) => {
        const domainId = action.meta.arg;
        state.startErrorByDomainId[domainId] = null;
        if (!state.startingDomainIds.includes(domainId)) {
          state.startingDomainIds.push(domainId);
        }
      })
      .addCase(startDomainDocumentEmbedding.fulfilled, (state, action) => {
        const domainId = action.payload;
        state.startingDomainIds = state.startingDomainIds.filter((id) => id !== domainId);
        // Optimistically mark the domain busy so the button disables and polling begins on the
        // next tick, instead of waiting a full poll interval for the backend to say so.
        const existing = state.statusByDomainId[domainId];
        if (existing) {
          state.statusByDomainId[domainId] = { ...existing, is_running: true };
        }
      })
      .addCase(startDomainDocumentEmbedding.rejected, (state, action) => {
        const domainId = action.meta.arg;
        state.startingDomainIds = state.startingDomainIds.filter((id) => id !== domainId);
        state.startErrorByDomainId[domainId] = action.payload ?? "Request failed";
      })
      .addCase(fetchDomainDocumentEmbeddingStatus.pending, (state, action) => {
        const domainId = action.meta.arg;
        // Only the first read shows a spinner; a poll refreshing an already-loaded status must
        // not flip the tab back into its loading state.
        if (state.requestStatusByDomainId[domainId] === undefined) {
          state.requestStatusByDomainId[domainId] = STATUS.LOADING;
        }
      })
      .addCase(fetchDomainDocumentEmbeddingStatus.fulfilled, (state, action) => {
        const { domainId, status } = action.payload;
        state.requestStatusByDomainId[domainId] = STATUS.SUCCEEDED;
        state.loadErrorByDomainId[domainId] = null;
        state.statusByDomainId[domainId] = status;
      })
      .addCase(fetchDomainDocumentEmbeddingStatus.rejected, (state, action) => {
        const domainId = action.payload?.domainId ?? action.meta.arg;
        state.requestStatusByDomainId[domainId] = STATUS.FAILED;
        state.loadErrorByDomainId[domainId] = action.payload?.message ?? "Request failed";
      });
  },
});

export const { clearDomainDocumentEmbeddingStartError } = domainDocumentEmbeddingsSlice.actions;

export const domainDocumentEmbeddingsReducer = domainDocumentEmbeddingsSlice.reducer;

export function selectDomainDocumentEmbeddingStatus(domainId: number | null) {
  return (state: RootState): DomainDocumentEmbeddingStatus | null =>
    domainId === null ? null : (state.domainDocumentEmbeddings.statusByDomainId[domainId] ?? null);
}

export function selectDomainDocumentEmbeddingLoadError(domainId: number | null) {
  return (state: RootState): string | null =>
    domainId === null ? null : (state.domainDocumentEmbeddings.loadErrorByDomainId[domainId] ?? null);
}

export function selectDomainDocumentEmbeddingStartError(domainId: number | null) {
  return (state: RootState): string | null =>
    domainId === null
      ? null
      : (state.domainDocumentEmbeddings.startErrorByDomainId[domainId] ?? null);
}

export function selectIsDomainDocumentEmbeddingStarting(domainId: number | null) {
  return (state: RootState): boolean =>
    domainId !== null && state.domainDocumentEmbeddings.startingDomainIds.includes(domainId);
}
